import React, { useState, useMemo } from 'react';
import { Eye, EyeOff, Lock, Check, X } from 'lucide-react';
import { confirmSignIn } from 'aws-amplify/auth';

interface ForcePasswordChangeProps {
  onSuccess: () => void;
}

interface PasswordRule {
  label: string;
  test: (pw: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { label: 'Mínimo 8 caracteres', test: (pw) => pw.length >= 8 },
  { label: 'Al menos una letra mayúscula', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'Al menos una letra minúscula', test: (pw) => /[a-z]/.test(pw) },
  { label: 'Al menos un número', test: (pw) => /\d/.test(pw) },
  { label: 'Al menos un carácter especial', test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw) },
];

const ForcePasswordChange: React.FC<ForcePasswordChangeProps> = ({ onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const ruleResults = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, passed: rule.test(newPassword) })),
    [newPassword]
  );

  const allRulesPassed = ruleResults.every((r) => r.passed);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!allRulesPassed) {
      setError('La contraseña no cumple todos los requisitos');
      return;
    }
    if (!passwordsMatch) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setIsLoading(true);
    try {
      await confirmSignIn({
        challengeResponse: newPassword,
      });
      onSuccess();
    } catch (err: any) {
      console.error('Force password change error:', err);
      const msg = err?.message || '';
      if (msg.includes('Password did not conform') || msg.includes('InvalidPasswordException')) {
        setError('La contraseña no cumple con los requisitos de seguridad de Cognito');
      } else if (msg.includes('Invalid session')) {
        setError('La sesión expiró. Por favor, vuelve a iniciar sesión.');
      } else {
        setError(msg || 'Error al cambiar la contraseña. Intenta nuevamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="mx-auto w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4">
          <Lock className="h-7 w-7 text-amber-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Cambio de contraseña</h2>
        <p className="mt-2 text-sm text-gray-600">
          Por seguridad, debes establecer una nueva contraseña para continuar.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
            Nueva contraseña
          </label>
          <div className="relative">
            <input
              id="new-password"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Mínimo 8 caracteres"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
            Confirmar contraseña
          </label>
          <div className="relative">
            <input
              id="confirm-password"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Repite la contraseña"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {confirmPassword.length > 0 && (
            <div className={`mt-1 flex items-center gap-1 text-xs ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
              {passwordsMatch ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
              {passwordsMatch ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}
            </div>
          )}
        </div>

        {/* Password requirements checklist */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Requisitos</p>
          {ruleResults.map((rule, i) => (
            <div key={i} className={`flex items-center gap-2 text-sm ${rule.passed ? 'text-green-600' : 'text-gray-400'}`}>
              {rule.passed ? (
                <Check className="h-4 w-4 flex-shrink-0" />
              ) : (
                <div className="h-4 w-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
              )}
              <span>{rule.label}</span>
            </div>
          ))}
        </div>

        <button
          type="submit"
          disabled={isLoading || !allRulesPassed || !passwordsMatch}
          className="w-full py-2.5 px-4 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
        >
          {isLoading ? 'Cambiando...' : 'Establecer nueva contraseña'}
        </button>
      </form>
    </div>
  );
};

export default ForcePasswordChange;
